import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';

@Component({
	selector: 'app-distribution-chart',
	templateUrl: './distribution-chart.component.html',
})
export class DistributionChartComponent implements OnChanges {
	@Input() chartType = 'BatterySOCDistribution';
	@Input() title : string;
	@Input() options: { hasBatteryOptions: boolean, hasCapacityOptions: boolean } = {
		hasBatteryOptions: true, 
		hasCapacityOptions: true,
	};

	@Input() fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	minDate = this.fromDate;
	maxDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	@Input() toDate = this.maxDate; // yesterday

	batteryVoltagesOptions: any[] = [];
	@Input() selectedBatteryVoltage: string = 'all';

	batteryCapacitiesOptions: any[] = [];
	@Input() selectedBatteryCapacity: string = 'all';

	@Input() selectedBatteryVintage: any[] = ['all'];

	batteryManufacturersOptions: any[] = [];
	@Input() selectedBatteryManufacturer: string = 'all';
	
	@Output() prepareParentFiltersOptions = new EventEmitter<{ batteryVoltagesOptions: any[], batteryCapacitiesOptions: any[], batteryManufacturersOptions: any[] }>();
	@Output() chartDataReady = new EventEmitter<{ title: string, xAxisLabel: string, yAxisLabel: string, data: any[], totalEventsCount: number }>();
	siteId = 0;
	chartColumns = [];
	distributionChartData = [];
	chartConfig: any = {
		legend: { position: 'top' },
		bar: {
			groupWidth: 30
		},
		tooltip: {isHtml: true}
	};
	totalBatteriesCount: number = 0;
	steps: number[] = [];

	constructor(
		private route: ActivatedRoute,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private siteService: SitesService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;

		this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.minDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				this.getBatteryDistributionData();
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.['fromDate'])
			this.changeDate('fromDate', changes['fromDate'].currentValue);

		if (changes?.['toDate'])
			this.changeDate('toDate', changes['toDate'].currentValue);

		if (changes?.['selectedBatteryVoltage'] || changes?.['selectedBatteryCapacity'] || changes?.['selectedBatteryVintage'] || changes?.['selectedBatteryManufacturer'])
			this.getBatteryDistributionData();
	}

	setChartConfig() {
		let vAxisTitle = '';
		let hAxisTitle = '';
		let chartColumnsTitle = '';

		const ticks = [];
		for (let i = 0; i <= 100; i += 20) {
			ticks.push(i);
		}

		switch (this.chartType) {
			case 'BatterySOCDistribution':
				vAxisTitle = this.translate.instant('fleet_reports.inuse_cycles_for_each_soc');
				hAxisTitle = this.translate.instant('fleet_reports.soc_percent');
				chartColumnsTitle = this.translate.instant('fleet_reports.inuse_cycles_for_each_soc');

				const xTicks = [];
				for (let i = 5; i <= 75; i += 5) {
					xTicks.push(i);
				}

				this.chartConfig.vAxis = { title: vAxisTitle, format: '#\'%\'', ticks };
				this.chartConfig.hAxis = { title: hAxisTitle, format: '#\'%\'', ticks: xTicks};
				break;
			case 'BatteryChargingTempDistribution':
				vAxisTitle = this.translate.instant('fleet_reports.charge_cycles_for_each_temperature');
				hAxisTitle = this.translate.instant('fleet_reports.temperature_range');
				chartColumnsTitle = this.translate.instant('fleet_reports.charge_cycles_for_each_temperature');
				this.chartConfig.vAxis = { title: vAxisTitle,format: '#\'%\'', ticks: ticks };
				this.chartConfig.hAxis = { title: hAxisTitle, ticks: this.steps };
				break;
			case 'BatteryInUseTempDistribution':
				vAxisTitle = this.translate.instant('fleet_reports.in_use_cycles_for_each_temperature');
				hAxisTitle = this.translate.instant('fleet_reports.temperature_range');
				chartColumnsTitle = this.translate.instant('fleet_reports.in_use_cycles_for_each_temperature');
				this.chartConfig.vAxis = { title: vAxisTitle,format: '#\'%\'', ticks: ticks };
				this.chartConfig.hAxis = { title: hAxisTitle, ticks: this.steps };
				break;
			case 'BatteryPlugInDistribution':
				vAxisTitle = this.translate.instant('fleet_reports.batteries_for_each_daily_plug_in');
				hAxisTitle = this.translate.instant('fleet_reports.battery_plug_ins');
				chartColumnsTitle = this.translate.instant('fleet_reports.batteries_for_each_daily_plug_in');
				this.chartConfig.vAxis = { title: vAxisTitle,format: '#\'%\'', ticks: ticks };
				this.chartConfig.hAxis = { title: hAxisTitle, ticks: [{v: 0, f: '0'}, {v: 1, f: '1'}, {v: 2, f: '2'}, {v: 3, f: '3'}, {v: 4, f: '4'}, {v: 5, f: '5+'}], baselineColor: 'transparent' };
				break;
			default:
				break;
		}

		this.chartColumns = ["", chartColumnsTitle];

		this.chartDataReady.emit({
			title: this.title,
			xAxisLabel: this.chartConfig.hAxis?.title || '',
			yAxisLabel: this.chartConfig.vAxis?.title || '',
			data: this.distributionChartData,
			totalEventsCount: this.totalBatteriesCount
		});
	}

	changeDate(field, value) {
		this[field] = value;

		this.getBatteryDistributionData();
	}

	getBatteryDistributionData() {
		if (!this.siteId)
			return;

		const zoneDiff	= new Date().getTimezoneOffset() * -1;
		let fromDate:any	= new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate:any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		const options = {
			from_date: fromDate,
			to_date: toDate,
			battery_voltage: this.selectedBatteryVoltage,
			battery_capacity: this.selectedBatteryCapacity,
			battery_vintage: this.selectedBatteryVintage,
			battery_manufacturer: this.selectedBatteryManufacturer,
		};

		this.siteService.getBatteryDistributionData(this.siteId, this.chartType, options).subscribe((data: any) => {
			this.prepareDistributionData(data);
			this.batteryVoltagesOptions = this.commonService.prepareOptions(data.batteryVoltages);
			this.batteryCapacitiesOptions = this.commonService.prepareOptions(data.batteryCapacities);
			this.batteryManufacturersOptions = this.commonService.prepareOptions(data.batteryManufacturers);

			this.prepareParentFiltersOptions.emit({
				batteryVoltagesOptions: this.batteryVoltagesOptions,
				batteryCapacitiesOptions: this.batteryCapacitiesOptions,
				batteryManufacturersOptions: this.batteryManufacturersOptions,
			});

			this.steps = data.chartSteps || [];
			this.setChartConfig();
		});
	}

	prepareDistributionData(data: any) {
		const chartData = [];
		let index = 0;

		let totalUnits = data.totalCyclesCount || 0;
		if (this.chartType === 'BatteryPlugInDistribution')
			totalUnits = data.totalBatteriesCount || 0;
 
		for (const step of data.chartSteps || []) {
			const stepPercent = totalUnits ? (data.chartData[index] / totalUnits) * 100 : 0;
			chartData.push([step, +(stepPercent).toFixed(2)]);
			index++;
		}
		this.totalBatteriesCount = data.totalBatteriesCount;
		this.distributionChartData = chartData;
	}

}
